import React, { useMemo, useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import EmptyState from '../../shared/components/EmptyState';
import { date } from '../../shared/formatters';
import { portalApi } from '../../lib/api';

export default function MessagesInbox() {
  const { data, add, patchLocal } = useData();
  const { notify } = useToast();
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [filter, setFilter] = useState('all'); // all | client | engineer | unread
  const aiEnabled = Boolean(data.settings?.ai?.enabled);
  const allThreads = useMemo(() => data.clients.map((client) => ({ client, messages: data.messages.filter((message) => message.client_id === client.id) })).filter((thread) => thread.messages.length).sort((a, b) => Number(b.messages.some((message) => !message.read_at)) - Number(a.messages.some((message) => !message.read_at))), [data]);

  // update_tasks.md §7.4 — filter chips: All / Client / Engineer / Unread
  // (read-only query filters on messages.sender + read state).
  const threads = useMemo(() => allThreads.filter((thread) => {
    const senders = new Set(thread.messages.map((message) => message.sender));
    if (filter === 'client') return senders.has('client');
    if (filter === 'engineer') return senders.has('engineer');
    if (filter === 'unread') return thread.messages.some((message) => !message.read_at && message.sender === 'client');
    return true;
  }), [allThreads, filter]);

  const filterChips = [
    ['all', 'All'],
    ['client', 'Client'],
    ['engineer', 'Engineer'],
    ['unread', 'Unread'],
  ];

  function openThread(thread) {
    setSelected(thread.client.id);
    const unread = thread.messages.filter((message) => !message.read_at);
    if (!unread.length) return;
    // Read-state goes through portal-api (backend.md §6.3) — one auditable path.
    unread.forEach(async (message) => {
      try {
        await portalApi.markMessagesRead(message.id);
        patchLocal('messages', message.id, { read_at: new Date().toISOString() });
      } catch {
        // Non-blocking — the thread still opens.
      }
    });
  }

  function send(event) {
    event.preventDefault();
    if (!draft.trim() || !selected) return;
    const thread = threads.find((item) => item.client.id === selected);
    // Duplicate-send guard: the last message in the thread identical to
    // this draft is almost always an accidental re-send (AI drafts are
    // identical for an unchanged thread) — confirm before creating it.
    const lastMessage = thread?.messages.at(-1);
    if (lastMessage && lastMessage.sender === 'agency' && lastMessage.body === draft.trim()
      && !window.confirm('This exact message was already sent to this client. Send it again?')) {
      return;
    }
    add('messages', { client_id: selected, sender: 'agency', body: draft });
    setDraft('');
    notify('Message sent');
  }

  // AI reply assist (pass 7 §7B): server drafts a reply from the thread
  // transcript into the compose box — human reviews before sending.
  async function draftReply() {
    if (!selected || drafting) return; // Prevent duplicate calls
    setDrafting(true);
    try {
      const result = await portalApi.aiSuggestReply(selected);
      setDraft(result.reply || '');
    } catch (err) {
      notify(err.message || 'Could not draft a reply', 'error');
    } finally {
      setDrafting(false);
    }
  }

  const thread = threads.find((item) => item.client.id === selected);

  return <div className="standalone-page"><header className="simple-header"><div><span className="eyebrow">Cross-client inbox</span><h1>Messages</h1></div></header><div className="filter-chips" role="group" aria-label="Filter messages">{filterChips.map(([value, label]) => <button key={value} className={`chip ${filter === value ? 'chip-active' : ''}`} onClick={() => setFilter(value)}>{label}</button>)}</div>{!threads.length ? <EmptyState title="No messages yet" /> : <div className="message-inbox"><aside className="thread-list">{threads.map((item) => <button className={selected === item.client.id ? 'selected' : ''} onClick={() => openThread(item)} key={item.client.id}><b>{item.client.initials}</b><span><strong>{item.client.name}</strong><small>{item.messages.at(-1).body}</small></span>{item.messages.some((message) => !message.read_at) && <i />}</button>)}</aside><section className="panel inbox-thread">{!thread ? <EmptyState title="Select a thread" message="Choose a client to view the conversation." /> : <><h3>{thread.client.name}</h3><div className="chat-list">{thread.messages.map((message) => <div className={`message ${message.sender}`} key={message.id}><small>{message.name} · {date(message.created_at)}</small><p>{message.body}</p></div>)}</div><form className="chat-compose" onSubmit={send}><input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Reply to client…" /><button type="button" className="ai-draft-button" onClick={draftReply} disabled={drafting || !aiEnabled} title={aiEnabled ? 'AI draft reply' : 'Enable AI in Settings to draft replies'}>{drafting ? '…' : '✦ AI'}</button><button>Send</button></form>{!aiEnabled && <p className="field-hint">AI drafts are off — enable them in Settings → AI.</p>}</>}</section></div>}</div>;
}
